import { zodResolver } from '@hookform/resolvers/zod'
import type { FC, ReactElement } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'
import { ErrorBox } from '@/components/commons/error-box'
import { Header } from '@/components/commons/header'

import { AlertCircleIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { BackgroundStep } from '@/features/experience-shell/components/background-step'
import { ExperienceStep } from '@/features/experience-shell/components/experience-step'
import { ItineraryStep } from '@/features/experience-shell/components/itinerary-step'
import { LegalStep } from '@/features/experience-shell/components/legal-step'
import { LocationStep } from '@/features/experience-shell/components/location-step'
import { MerchandisingStep } from '@/features/experience-shell/components/merchandising-step'
import { PhotosStep } from '@/features/experience-shell/components/photos-step'
import { PricingDiscountStep } from '@/features/experience-shell/components/pricing-discount-step'
import { StepsNavigation } from '@/features/experience-shell/components/steps-navigation'
import { SuccessView } from '@/features/experience-shell/components/success-view'
import {
	type ShellUpgradeFormDefaultValues,
	ShellUpgradeFormSchema
} from '@/features/experience-shell/form-schemas'
import { useGetExperience } from '@/features/experience-shell/queries/use-get-experience'
import {
	type Experience,
	ExperienceStatus,
	ShellStepKeyName
} from '@/features/experience-shell/types'
import { makeShellFormDefaultValues } from '@/features/experience-shell/utils'
import { raise } from '@/helpers/utils'
import { useURLParsedParams } from '@/hooks/use-url-parsed-params'
import { useTranslation } from '@/translations/i18n'

export const ExperienceShellUpgrade = () => {
	const { t } = useTranslation()
	const params = useURLParsedParams(
		z.object({ id: z.coerce.number().int().positive() })
	)
	const experienceQuery = useGetExperience(
		params.success ? params.data.id : raise('id is nullish')
	)

	if (experienceQuery.isPending) {
		return (
			<div className='flex h-full w-full items-center justify-center'>
				<Spinner size={32} />
			</div>
		)
	}
	if (experienceQuery.isError) {
		return (
			<ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
		)
	}
	return <ExperienceShellContent experience={experienceQuery.data} />
}

const ExperienceShellContent: FC<{ experience: Experience }> = ({
	experience
}) => {
	const defaultValues = makeShellFormDefaultValues(experience)
	const form = useForm({
		mode: 'onChange',
		resolver: zodResolver(ShellUpgradeFormSchema),
		defaultValues: defaultValues as unknown as ShellUpgradeFormDefaultValues
	})

	const isShellInProgress =
		experience.status === ExperienceStatus.enum.pending_shell_submission ||
		experience.status === ExperienceStatus.enum.pending_shell_creation ||
		experience.status === ExperienceStatus.enum.pending_merchandising

	const stepKeyName = ShellStepKeyName.catch('experience').parse(
		experience.step.keyName
	)
	const getStepComponent = (stepKeyName: ShellStepKeyName): ReactElement => {
		switch (stepKeyName) {
			case 'background':
				return (
					<BackgroundStep
						experienceId={experience.id}
						isUpgrade={true}
					/>
				)
			case 'merchandising':
				return (
					<MerchandisingStep
						experienceId={experience.id}
						isUpgrade={true}
					/>
				)
			case 'location':
				return (
					<LocationStep
						experienceId={experience.id}
						isUpgrade={true}
					/>
				)
			case 'photos':
				return (
					<PhotosStep
						gallery={experience.gallery}
						isUpgrade={true}
						itineraries={experience.itineraries}
					/>
				)
			case 'itinerary':
				return (
					<ItineraryStep
						experienceId={experience.id}
						galleryId={experience.gallery.id}
						isUpgrade={true}
						itineraries={experience.itineraries}
					/>
				)
			case 'pricing_discounts':
				return (
					<PricingDiscountStep
						experienceId={experience.id}
						isUpgrade={true}
						itineraries={experience.itineraries}
					/>
				)
			case 'settings':
				return (
					<PricingDiscountStep
						experienceId={experience.id}
						isUpgrade={true}
						itineraries={experience.itineraries}
					/>
				)
			case 'legal':
				return (
					<LegalStep
						experienceId={experience.id}
						galleryImagesCount={experience.gallery.images.length}
						isUpgrade={true}
					/>
				)
			default:
				return (
					<ExperienceStep
						experienceId={experience.id}
						isUpgrade={true}
					/>
				)
		}
	}

	return (
		<FormProvider {...form}>
			<div className='grid h-full grid-cols-[280px_minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden text-neutral-900 [&_aside]:row-[2/-1] [&_header]:col-span-full [&_header]:border-neutral-300 [&_header]:border-b'>
				<Header
				/* endSlot={
                        experience.airtableLink && (
                            <ButtonLink variant="tertiary" to={experience.airtableLink} target="_blank">
                                <AirtableIcon className='fill-neutral-400' />
                                {t('common:airtable_cta')}
                            </ButtonLink>
                        )
                    } */
				/>
				{isShellInProgress && (
					<>
						<StepsNavigation
							currentStep={stepKeyName}
							experienceId={experience.id}
							galleryImagesCount={
								experience.gallery.images.length
							}
							isUpgrade={true}
							itineraries={experience.itineraries}
						/>
						{getStepComponent(stepKeyName)}
					</>
				)}
				{!isShellInProgress && <SuccessView isUpgrade={true} />}
			</div>
		</FormProvider>
	)
}
